import { gql } from "@/gql/__generated__";
import {
  AppApiKeysQuery,
  AppDeploymentApiKeyCreateMutation,
  AppSubscriptionApiKeyCreateMutation,
  SubscriptionAppApiKeysQuery
} from "@/gql/__generated__/graphql";
import { useMutation } from "@apollo/client";
import { useUserActiveOrganization } from "../user/useUserActiveOrganization";
import { APP_API_KEYS, Origin, SUBSCRIPTION_APP_API_KEYS } from "./appAPIKeys";

const APP_DEPLOYMENT_API_KEY_CREATE = gql(`
  mutation appDeploymentAPIKeyCreate($id: ID!, $name: String!) {
    appDeploymentAPIKeyCreate(deployID: $id, name: $name) {
      ...APIKeyFragment
    }
  }
`);

const APP_SUBSCRIPTION_API_KEY_CREATE = gql(`
  mutation appSubscriptionAPIKeyCreate($id: ID!, $name: String!) {
    appSubscriptionAPIKeyCreate(subscriptionId: $id, name: $name) {
      ...APIKeyFragment
    }
  }
`);

const originMap = {
  [Origin.Organization]: APP_DEPLOYMENT_API_KEY_CREATE,
  [Origin.Subscription]: APP_SUBSCRIPTION_API_KEY_CREATE
} as const;

interface Props {
  id: string | undefined;
  appSlug: string | undefined;
  subscriptionId: string | undefined;
  origin: Origin | undefined;
}

export function useAppAPIKeyCreate({
  id = "",
  appSlug,
  subscriptionId,
  origin = Origin.Organization
}: Props) {
  const { activeOrganization } = useUserActiveOrganization();

  return useMutation<
    AppDeploymentApiKeyCreateMutation | AppSubscriptionApiKeyCreateMutation
  >(originMap[origin], {
    variables: { id },
    update(cache, { data }) {
      if (!data || !activeOrganization) return;

      const newAPIKey =
        "appDeploymentAPIKeyCreate" in data
          ? data.appDeploymentAPIKeyCreate
          : data.appSubscriptionAPIKeyCreate;

      if (newAPIKey.__typename !== "AppAPIKey") return;

      if (origin === Origin.Organization && appSlug)
        cache.updateQuery(
          {
            query: APP_API_KEYS,
            variables: { organizationSlug: activeOrganization.slug, appSlug }
          },
          (cacheData) => {
            if (!cacheData?.app?.defaultDeployment) return cacheData;

            const updatedAppAPIKeys = [
              ...cacheData.app.defaultDeployment.apiKeys,
              newAPIKey
            ];

            const updatedApp: AppApiKeysQuery = {
              __typename: "Query",
              app: {
                ...cacheData.app,
                defaultDeployment: {
                  ...cacheData.app.defaultDeployment,
                  apiKeys: updatedAppAPIKeys
                }
              }
            };

            return updatedApp;
          }
        );

      if (origin === Origin.Subscription && subscriptionId)
        cache.updateQuery(
          {
            query: SUBSCRIPTION_APP_API_KEYS,
            variables: { subscriptionId }
          },
          (cacheData) => {
            if (!cacheData) return cacheData;

            const updatedAppAPIKeys = [
              ...cacheData.subscription.apiKeys,
              newAPIKey
            ];

            const updatedApp: SubscriptionAppApiKeysQuery = {
              __typename: "Query",
              subscription: {
                ...cacheData.subscription,
                apiKeys: updatedAppAPIKeys
              }
            };

            return updatedApp;
          }
        );
    }
  });
}
